import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Lock, UserRound } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { GENDER_OPTIONS } from '@/constants/genderOptions';
import { NIGERIAN_STATES } from '@/constants/nigerianStates';
import { useUserAuth } from '@/context/UserAuthContext';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { notify, userAccountService } from '@/services';
import type { PublicUser } from '@/types/auth';
import { cn } from '@/utils/cn';
import { formatDateForDisplay, formatDateForInput } from '@/utils/dates';

type ProfileForm = {
  firstName: string;
  lastName: string;
  state: string;
  gender: string;
  dateOfBirth: string;
  nin: string;
  bvn: string;
};

function profileToForm(user: PublicUser | null): ProfileForm {
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    state: user?.state ?? '',
    gender: user?.gender ?? '',
    dateOfBirth: formatDateForInput(user?.dateOfBirth),
    nin: user?.nin ?? '',
    bvn: user?.bvn ?? '',
  };
}

function formatGender(value?: string | null) {
  if (!value) return '—';
  return GENDER_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function getMissingFields(user: PublicUser | null) {
  if (!user) return [];
  const missing: string[] = [];
  if (!user.state) missing.push('State');
  if (!user.gender) missing.push('Gender');
  if (!formatDateForInput(user.dateOfBirth)) missing.push('Date of birth');
  return missing;
}

export function UserProfilePage() {
  const { user, persistUser, refreshProfile } = useUserAuth();
  const { openPinModal } = useDashboardUi();
  const { loading, run } = useAsyncAction();
  const [form, setForm] = useState<ProfileForm>(() => profileToForm(user));

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    setForm(profileToForm(user));
  }, [user]);

  const missingFields = useMemo(() => getMissingFields(user), [user]);

  function updateField<K extends keyof ProfileForm>(field: K, value: ProfileForm[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleReset() {
    setForm(profileToForm(user));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const payload: Record<string, string> = {};
    if (form.firstName.trim() !== user?.firstName) payload.firstName = form.firstName.trim();
    if (form.lastName.trim() !== user?.lastName) payload.lastName = form.lastName.trim();
    if ((form.state || '') !== (user?.state || '')) payload.state = form.state;
    if ((form.gender || '') !== (user?.gender || '')) payload.gender = form.gender;
    if (formatDateForInput(form.dateOfBirth) !== formatDateForInput(user?.dateOfBirth)) {
      payload.dateOfBirth = form.dateOfBirth;
    }
    if ((form.nin.trim() || '') !== (user?.nin || '')) payload.nin = form.nin.trim();
    if ((form.bvn.trim() || '') !== (user?.bvn || '')) payload.bvn = form.bvn.trim();

    if (Object.keys(payload).length === 0) {
      notify.info('No changes to save');
      return;
    }

    await run(async () => {
      try {
        const res = await userAccountService.updateProfile(payload);
        if (!res.success || !res.data?.user) throw new Error(res.message || 'Failed to update profile');
        persistUser(res.data.user);
        notify.apiSuccess(res.message, 'Profile updated successfully');
      } catch (err) {
        notify.apiError(err, 'Failed to update profile');
      }
    });
  }

  return (
    <div className="space-y-5 pb-16 sm:pb-20">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">Profile</h1>
        <p className="text-sm text-brand-muted">Manage your account details and verification info</p>
      </div>

      <ProfileSummaryStrip user={user} />

      {missingFields.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Complete your profile</p>
            <p className="mt-0.5 text-xs text-amber-800">Please add: {missingFields.join(', ')}</p>
          </div>
        </div>
      )}

      {!user?.pinEnabled && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-red-800">Transaction PIN not set</p>
              <p className="text-xs text-red-700">Set a PIN to authorize wallet transactions.</p>
            </div>
          </div>
          <Button size="sm" onClick={openPinModal}>
            Set PIN
          </Button>
        </div>
      )}

      <Card className="p-5 sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/5 text-brand-navy">
            <UserRound className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-bold text-brand-navy">Update your profile</h2>
            <p className="text-xs text-brand-muted">Email and phone are fixed — edit everything else below</p>
          </div>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              required
            />
            <Input
              label="Last name"
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              required
            />
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
            <p className="text-xs font-medium text-brand-muted">Account identifiers (read-only)</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Input label="Email" type="email" value={user?.email ?? ''} disabled hint="Contact support to change email" />
              <Input label="Phone" value={user?.phone ?? ''} disabled hint="Contact support to change phone" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label htmlFor="state" className="block text-sm font-medium text-brand-navy">
                State {!user?.state && <span className="text-primary">*</span>}
              </label>
              <select
                id="state"
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
                className={cn(
                  'block w-full rounded-xl border-gray-200 bg-white px-4 py-2.5 text-sm text-brand-navy shadow-sm focus:border-primary focus:ring-primary',
                  !user?.state && 'border-amber-300 ring-1 ring-amber-200',
                )}
              >
                <option value="">Select state</option>
                {NIGERIAN_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="gender" className="block text-sm font-medium text-brand-navy">
                Gender {!user?.gender && <span className="text-primary">*</span>}
              </label>
              <select
                id="gender"
                value={form.gender}
                onChange={(e) => updateField('gender', e.target.value)}
                className={cn(
                  'block w-full rounded-xl border-gray-200 bg-white px-4 py-2.5 text-sm text-brand-navy shadow-sm focus:border-primary focus:ring-primary',
                  !user?.gender && 'border-amber-300 ring-1 ring-amber-200',
                )}
              >
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Date of birth"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => updateField('dateOfBirth', e.target.value)}
              className={!formatDateForInput(user?.dateOfBirth) ? 'border-amber-300 ring-1 ring-amber-200' : undefined}
            />
          </div>

          <div className="rounded-xl border border-gray-100 bg-brand-surface/50 p-4">
            <p className="text-sm font-semibold text-brand-navy">Optional — NIN & BVN</p>
            <p className="mt-0.5 text-xs text-brand-muted">For verification services when needed</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Input
                label="NIN"
                value={form.nin}
                onChange={(e) => updateField('nin', e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="11 digits"
                maxLength={11}
              />
              <Input
                label="BVN"
                value={form.bvn}
                onChange={(e) => updateField('bvn', e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="11 digits"
                maxLength={11}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-4">
            <Button type="submit" loading={loading} loadingText="Saving…">
              Update profile
            </Button>
            <Button type="button" variant="outline" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </form>
      </Card>

      <div aria-hidden className="h-8 sm:h-12" />
    </div>
  );
}

function ProfileSummaryStrip({ user }: { user: PublicUser | null }) {
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-soft">
      <div className="h-1 bg-gradient-brand" />
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-brand text-sm font-bold text-white sm:h-12 sm:w-12">
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate font-bold text-brand-navy">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="truncate text-xs text-brand-muted">
              {user?.email || '—'} · {user?.phone || '—'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <SummaryBadge capitalize>{user?.userType ?? '—'}</SummaryBadge>
          <SummaryBadge variant={user?.status === 'active' ? 'success' : 'muted'} capitalize>
            {user?.status ?? '—'}
          </SummaryBadge>
          <SummaryBadge variant={user?.pinEnabled ? 'success' : 'warning'}>
            PIN {user?.pinEnabled ? 'enabled' : 'not set'}
          </SummaryBadge>
          {user?.gender && <SummaryBadge>{formatGender(user.gender)}</SummaryBadge>}
          {formatDateForInput(user?.dateOfBirth) && (
            <SummaryBadge>DOB {formatDateForDisplay(user?.dateOfBirth)}</SummaryBadge>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryBadge({
  children,
  variant = 'default',
  capitalize,
}: {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'muted';
  capitalize?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold sm:text-xs',
        capitalize && 'capitalize',
        variant === 'default' && 'bg-brand-navy/5 text-brand-navy',
        variant === 'success' && 'bg-secondary/10 text-secondary',
        variant === 'warning' && 'bg-amber-100 text-amber-800',
        variant === 'muted' && 'bg-brand-surface text-brand-muted',
      )}
    >
      {children}
    </span>
  );
}
